// Label Templates — central configuration page for label printing.
//
// Replaces the legacy /label + /add-label + /pool-printing screens. The
// desktop printer app consumes these defaults automatically; mobile +
// web just need to pick which template and upload the company logo.
//
// Three sections:
//   1. Company Logo — preview + upload/remove
//   2. Peças — grid of templates, "Definir como padrão" per card
//   3. Viaturas — same, for vehicles
//
// Uses the shared service layer (LabelServices) and visual language
// that matches the desktop app (green + purple, glass-like cards).

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

import Utils from "../../utils";
import {
  listPrintTemplates,
  setDefaultPrintTemplate,
  uploadCompanyPrintLogo,
  deleteCompanyPrintLogo,
  deletePrintTemplate,
  duplicatePrintTemplate,
} from "../../services/LabelServices";
import "./label-templates.css";

const LabelTemplates = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [companyLogoUrl, setCompanyLogoUrl] = useState(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await listPrintTemplates();
      const payload = data?.data || {};
      setTemplates(payload.templates || []);
      setCompanyLogoUrl(payload.company_logo_url || null);
    } catch (err) {
      toast.error(Utils.getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const parts = useMemo(
    () => templates.filter((t) => t.entity_type === "part"),
    [templates]
  );
  const vehicles = useMemo(
    () => templates.filter((t) => t.entity_type === "vehicle"),
    [templates]
  );

  const handleSetDefault = async (tmpl) => {
    try {
      setSavingId(tmpl.id);
      await setDefaultPrintTemplate({
        template_id: tmpl.id,
        entity_type: tmpl.entity_type,
      });
      toast.success(`"${tmpl.name}" definido como padrão`);
      await load();
    } catch (err) {
      toast.error(Utils.getErrorMessage(err));
    } finally {
      setSavingId(null);
    }
  };

  const handleDuplicate = async (tmpl) => {
    try {
      const r = await duplicatePrintTemplate({ id: tmpl.id });
      const newId = r.data?.data?.id;
      toast.success("Template duplicado.");
      if (newId) {
        navigate(`/label-templates/editor/${newId}`);
      } else {
        await load();
      }
    } catch (err) {
      toast.error(Utils.getErrorMessage(err));
    }
  };

  const handleDelete = async (tmpl) => {
    if (!window.confirm(`Apagar template "${tmpl.name}"?`)) return;
    try {
      await deletePrintTemplate({ id: tmpl.id });
      toast.success("Template apagado.");
      await load();
    } catch (err) {
      toast.error(Utils.getErrorMessage(err));
    }
  };

  const handleLogoSelect = () => fileInputRef.current?.click();

  const handleLogoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const max = 2 * 1024 * 1024;
    if (file.size > max) {
      toast.error("Logotipo excede 2MB.");
      return;
    }
    const formData = new FormData();
    formData.append("logo", file);
    try {
      setLogoBusy(true);
      const { data } = await uploadCompanyPrintLogo(formData);
      setCompanyLogoUrl(data?.data?.print_logo_url || null);
      toast.success("Logotipo actualizado");
    } catch (err) {
      toast.error(Utils.getErrorMessage(err));
    } finally {
      setLogoBusy(false);
      e.target.value = "";
    }
  };

  const handleLogoRemove = async () => {
    if (!window.confirm("Remover o logotipo actual?")) return;
    try {
      setLogoBusy(true);
      await deleteCompanyPrintLogo();
      setCompanyLogoUrl(null);
      toast.info("Logotipo removido");
    } catch (err) {
      toast.error(Utils.getErrorMessage(err));
    } finally {
      setLogoBusy(false);
    }
  };

  return (
    <div className="lt-page">
      <div className="lt-hero">
        <div className="lt-hero-left">
          <h1>{t("label_templates")}</h1>
          <p>
            Gestão centralizada dos templates usados pela aplicação TP
            Software. O logotipo e os templates padrão aqui definidos são
            aplicados automaticamente a todas as etiquetas impressas.
          </p>
        </div>
        <div className="lt-hero-orb" aria-hidden="true">
          <div className="lt-orb" />
        </div>
      </div>

      {/* Logo section */}
      <section className="lt-card">
        <header className="lt-card-head">
          <i className="bi bi-image-fill lt-card-head-ic" />
          <div>
            <h2>Logotipo da empresa</h2>
            <small>
              Aparece nas etiquetas onde o template tem o campo{" "}
              <code>company_logo</code>. Recomendado PNG transparente,
              mínimo 200×80 px, máx. 2MB.
            </small>
          </div>
        </header>
        <div className="lt-logo-row">
          <div className="lt-logo-preview">
            {companyLogoUrl ? (
              <img src={companyLogoUrl} alt="Logotipo" />
            ) : (
              <div className="lt-logo-empty">
                <i className="bi bi-upload" />
                <span>Sem logotipo definido</span>
              </div>
            )}
          </div>
          <div className="lt-logo-actions">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/svg+xml"
              style={{ display: "none" }}
              onChange={handleLogoChange}
            />
            <button
              type="button"
              className="lt-btn lt-btn-primary"
              onClick={handleLogoSelect}
              disabled={logoBusy}
            >
              <i className="bi bi-cloud-arrow-up" />
              {companyLogoUrl ? "Substituir" : "Carregar"}
            </button>
            {companyLogoUrl && (
              <button
                type="button"
                className="lt-btn lt-btn-ghost"
                onClick={handleLogoRemove}
                disabled={logoBusy}
              >
                <i className="bi bi-trash" />
                Remover
              </button>
            )}
          </div>
        </div>
      </section>

      <div className="lt-actions-bar">
        <button
          type="button"
          className="lt-btn lt-btn-primary"
          onClick={() => navigate("/label-templates/editor/new")}
        >
          <i className="bi bi-plus-lg" /> Criar template
        </button>
      </div>

      {loading ? (
        <div className="lt-card lt-loading">A carregar templates…</div>
      ) : (
        <>
          <TemplateGroup
            title="Peças"
            icon="bi-wrench-adjustable"
            accent="green"
            items={parts}
            onSetDefault={handleSetDefault}
            savingId={savingId}
            companyLogoUrl={companyLogoUrl}
            onEdit={(t) => navigate(`/label-templates/editor/${t.id}`)}
            onDuplicate={handleDuplicate}
            onDelete={handleDelete}
          />
          <TemplateGroup
            title="Viaturas"
            icon="bi-car-front-fill"
            accent="purple"
            items={vehicles}
            onSetDefault={handleSetDefault}
            savingId={savingId}
            companyLogoUrl={companyLogoUrl}
            onEdit={(t) => navigate(`/label-templates/editor/${t.id}`)}
            onDuplicate={handleDuplicate}
            onDelete={handleDelete}
          />
        </>
      )}
    </div>
  );
};

const TemplateGroup = ({
  title,
  icon,
  accent,
  items,
  onSetDefault,
  savingId,
  companyLogoUrl,
  onEdit,
  onDuplicate,
  onDelete,
}) => {
  return (
    <section className={`lt-card lt-group lt-group-${accent}`}>
      <header className="lt-card-head">
        <i className={`bi ${icon} lt-card-head-ic`} />
        <div>
          <h2>{title}</h2>
          <small>
            {items.length} template{items.length === 1 ? "" : "s"}{" "}
            disponíve{items.length === 1 ? "l" : "is"}
          </small>
        </div>
      </header>
      {items.length === 0 ? (
        <div className="lt-empty">
          Sem templates para {title.toLowerCase()}.
        </div>
      ) : (
        <div className="lt-grid">
          {items.map((tmpl) => (
            <TemplateCard
              key={tmpl.id}
              tmpl={tmpl}
              accent={accent}
              onSetDefault={onSetDefault}
              saving={savingId === tmpl.id}
              companyLogoUrl={companyLogoUrl}
              onEdit={onEdit}
              onDuplicate={onDuplicate}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}
    </section>
  );
};

const TemplateCard = ({
  tmpl,
  accent,
  onSetDefault,
  saving,
  companyLogoUrl,
  onEdit,
  onDuplicate,
  onDelete,
}) => {
  const isDefault = tmpl.is_default_part || tmpl.is_default_vehicle;
  return (
    <article
      className={`lt-tmpl${isDefault ? " lt-tmpl-default" : ""} lt-tmpl-${accent}`}
    >
      <div className="lt-tmpl-head">
        <span
          className={`lt-pill ${tmpl.is_system ? "lt-pill-neutral" : "lt-pill-green"}`}
        >
          {tmpl.is_system ? "Sistema" : "Personalizado"}
        </span>
        <span className={`lt-pill lt-pill-${engineTone(tmpl.engine)}`}>
          {engineLabel(tmpl.engine)}
        </span>
        {isDefault && (
          <span className="lt-pill lt-pill-default">
            <i className="bi bi-check-lg" /> Padrão
          </span>
        )}
      </div>
      <h3 className="lt-tmpl-name">{tmpl.name}</h3>
      {tmpl.description && (
        <p className="lt-tmpl-desc">{tmpl.description}</p>
      )}
      <TemplatePreview tmpl={tmpl} accent={accent} logoUrl={companyLogoUrl} />
      <div className="lt-tmpl-meta">
        <span>
          <i className="bi bi-grid-3x3-gap" /> {tmpl.element_count} elementos
        </span>
        <span>
          <i className="bi bi-speedometer2" /> {tmpl.dpi} DPI
        </span>
      </div>
      <div className="lt-tmpl-cta">
        {isDefault ? (
          <span className="lt-tmpl-active">
            <i className="bi bi-check-circle-fill" /> Activo
          </span>
        ) : (
          <button
            type="button"
            className="lt-btn lt-btn-ghost"
            onClick={() => onSetDefault(tmpl)}
            disabled={saving}
          >
            {saving ? (
              "A guardar…"
            ) : (
              <>
                <i className="bi bi-star" /> Definir como padrão
              </>
            )}
          </button>
        )}
      </div>
      <div className="lt-tmpl-actions">
        {!tmpl.is_system && (
          <button
            type="button"
            className="lt-btn-icon"
            title="Editar"
            onClick={() => onEdit?.(tmpl)}
          >
            <i className="bi bi-pencil" />
          </button>
        )}
        <button
          type="button"
          className="lt-btn-icon"
          title="Duplicar"
          onClick={() => onDuplicate?.(tmpl)}
        >
          <i className="bi bi-files" />
        </button>
        {!tmpl.is_system && (
          <button
            type="button"
            className="lt-btn-icon lt-btn-icon-danger"
            title="Apagar"
            onClick={() => onDelete?.(tmpl)}
          >
            <i className="bi bi-trash" />
          </button>
        )}
      </div>
    </article>
  );
};

const TemplatePreview = ({ tmpl, accent, logoUrl }) => {
  const aspect = tmpl.width_mm / (tmpl.height_mm || 1);
  return (
    <div className="lt-preview" style={{ aspectRatio: `${aspect}` }}>
      <div className="lt-preview-grid" aria-hidden="true" />
      {logoUrl ? (
        <img src={logoUrl} alt="" className="lt-preview-logo-real" />
      ) : (
        <div className={`lt-preview-logo lt-preview-logo-${accent}`} />
      )}
      <div className="lt-preview-title" />
      <div className="lt-preview-bar" />
      <div className="lt-preview-dims">
        {tmpl.width_mm}×{tmpl.height_mm} mm
      </div>
    </div>
  );
};

const engineLabel = (e) => {
  switch (e) {
    case "zpl":
      return "Zebra";
    case "brother_raster":
      return "Brother";
    default:
      return "Universal";
  }
};
const engineTone = (e) => {
  switch (e) {
    case "zpl":
      return "purple";
    case "brother_raster":
      return "warning";
    default:
      return "neutral";
  }
};

export default LabelTemplates;
